import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from '../../../../../app/shared/state/app/app.service';
import { ExpressOrderResponse } from '../../../../../app/shared/models/express-order-model';
import { PagePath } from '../../../../../app/shared/models/page-model';
import { ProductDefinitionService } from '../../../../../app/shared/services/product-definition/product-definition.service';
import { QuestionnaireRoutingService } from '../../../../../app/shared/services/questionnaire-routing/questionnaire-routing.service';
import { QuestionnaireService } from '../../../../../app/shared/services/questionnaire/questionnaire.service';
import { TrackingService } from '../../../../../app/shared/services/tracking/tracking.service';
import { ProductConfigurationIds } from '../../../../../app/shared/models/cart-model';
import { IUserCart } from '@legalzoom/cart-sdk';
import * as moment from 'moment';

@Component({
  selector: 'app-order-confirmation',
  templateUrl: './order-confirmation.component.html',
  styleUrls: ['./order-confirmation.component.scss']
})
export class OrderConfirmationComponent implements OnInit {

  public orderId: number;
  public lwtOrder: ExpressOrderResponse;
  public tmsOrder: ExpressOrderResponse;
  public gsuiteOrder: ExpressOrderResponse;
  public gsuitePackageType: string;
  public gsuiteNumSeats: number;
  public checkoutEmail: string;
  public isPaymentError: boolean;
  public businessName: string;
  public orderDate: string;
  public location: string;
  public type = 'LLC';
  public orderDeclined = false;
  public firstName: string;

  public hasCompanyName: boolean;
  public isThreePay: boolean;
  public hasRegisteredAgent: boolean;
  public hasLegalProtect: boolean;
  public hasSmartEmployer: boolean;
  public hasTotalCompliance: boolean;

  public installmentAmount: number;
  public llcAmount: number;
  public lwtAmount: number;
  public tsmAmount: number;
  public gsuiteAmount: number;
  public orderTotal: number;
  public chargedToday: number;
  public showWIPrice: boolean;

  constructor(
    private router: Router,
    private questionnaireRoutingService: QuestionnaireRoutingService,
    public questionnaireService: QuestionnaireService,
    private trackingService: TrackingService,
    private productDefinitionService: ProductDefinitionService,
    private appService: AppService
  ) {

    this.orderId = this.questionnaireService.llc.orderId;
    this.checkoutEmail = this.questionnaireService.llc.checkoutEmail;
    this.lwtOrder = this.questionnaireService.llc.LWTExpressOrderResponse;
    this.tmsOrder = this.questionnaireService.llc.TMSExpressOrderResponse;
    this.gsuiteOrder = this.questionnaireService.llc.GSuiteOrderResponse;
    this.gsuitePackageType = this.questionnaireService.llc.gsuitePackageType;
    this.gsuiteNumSeats = this.questionnaireService.llc.gsuiteNumSeats;
    this.businessName = this.questionnaireService.llc.entityName;
    this.location = this.questionnaireService.llc.entityState;

    this.hasCompanyName = !!this.questionnaireService.llc.entityName;

    if(this.appService.app.firstName) {
      this.firstName = this.appService.app.firstName;
    }

    if(this.appService.app.lwtCrossSellPaymentResponse) {
      this.lwtAmount = this.appService.app.lwtCrossSellPaymentResponse.amount;
    }

    if(this.appService.app.tmsCrossSellPaymentResponse) {
      this.tsmAmount = this.appService.app.tmsCrossSellPaymentResponse.amount;
    }

    // get the gsuite payment data to populate the line item
    if(this.appService.app.gSuiteCrossSellPaymentResponse) {
      this.gsuiteAmount = this.appService.app.gSuiteCrossSellPaymentResponse.amount;
    }

    if(this.appService.payment) {
      const paymentStatus = this.appService.payment.transactionStatus; // 3 = success, show error for anything except success
      if(paymentStatus !== 3) {
        this.orderDeclined = true;
      }
      this.orderDate = moment(this.appService.payment.creationDate).format('M/D/YY');
    }

    if( this.appService.cart) {
      this.updateDisclaimers(this.appService.cart);
    }

    this.isThreePay = this.questionnaireService.llc.is3Pay;
    if(this.isThreePay) {
      this.installmentAmount = this.appService.app.getCartBalanceByCartIdResponse.cartInstallments.secondInstallment.installmentAmount;
    }

    this.orderTotal = this.appService.app.getCartBalanceByCartIdResponse.totalAmount;
    if(this.isThreePay) {
      this.chargedToday = this.appService.app.getCartBalanceByCartIdResponse.cartInstallments.firstInstallment.installmentAmount;
    } else {
      this.chargedToday = this.orderTotal;
    }

    if ((this.lwtOrder && this.lwtOrder.PaymentStatus === 0) ||
      (this.tmsOrder && this.tmsOrder.PaymentStatus === 0) ||
      (this.gsuiteOrder && this.gsuiteOrder.PaymentStatus === 0)) {
      this.orderDeclined = true;
    }
  }

  ngOnInit() {
    this.trackingService.SetGA_Arrays(PagePath.OrderConfirmation);
    if (this.questionnaireService.llc.entityState === 'Wisconsin') {
      this.showWIPrice = true;
    } else {
      this.showWIPrice = false;
    }
  }

  getLLCPackage(): string {
    return this.productDefinitionService.getLLCPackageName(this.questionnaireService.llc.packageSelected);
  }

  save(): void {
    const nextPage = this.questionnaireRoutingService.getNextPage(PagePath.OrderConfirmation);
    this.router.navigate(['./' + nextPage]);
  }

  updateDisclaimers(cart: IUserCart){
    const llcItem = cart.cartItems.find(x => x.productConfigurationId === this.questionnaireService.llc.packageSelected);
    this.llcAmount = llcItem.amount;

    const smartEmployerItem = cart.cartItems.find(i => 
      i.productConfigurationId === ProductConfigurationIds.bapMonthlyLegal ||
      i.productConfigurationId === ProductConfigurationIds.bapMonthlyLegalWisconsin);
    if (smartEmployerItem) {
      this.hasSmartEmployer = true;
    }

    const legalProtectItem = cart.cartItems.find(i => 
      i.productConfigurationId === ProductConfigurationIds.bapMonthlyLegalProtect ||
      i.productConfigurationId === ProductConfigurationIds.bapMonthlyLegalProtectWisconsin);
    if (legalProtectItem) {
      this.hasLegalProtect = true;
    }    

    const registeredAgentItem = cart.cartItems.find(i => 
      ( i.productConfigurationId === ProductConfigurationIds.registeredAgent ) ||
      ( i.productConfigurationId === ProductConfigurationIds.registeredAgent_199 ) ||
      ( i.productConfigurationId === ProductConfigurationIds.registeredAgent_249 )
    );
    if (registeredAgentItem) {
      this.hasRegisteredAgent = true;
    }

    const totalComplianceItem = cart.cartItems.find(i => 
      ( i.productConfigurationId === ProductConfigurationIds.compliancePackage ));
    if (totalComplianceItem) {
      this.hasTotalCompliance = true;
    }
  }
}
